import React, { useId } from "react";
import { CHART_COLORS } from "@/features/investors/components/charts/palette";

type Props = {
    values: number[];
    labels: string[];
    color?: string;
    height?: number;
    min?: number;
    max?: number;
    formatValue: (value: number) => string;
    tipFor?: (index: number) => string;
};

export const AreaTrend: React.FC<Props> = ({
    values,
    labels,
    color = CHART_COLORS.blue,
    height = 200,
    min,
    max,
    formatValue,
    tipFor,
}) => {
    const gradientId = useId();
    if (values.length < 2) return null;

    const W = 640;
    const H = height;
    const P = { t: 18, r: 14, b: 28, l: 14 };
    const iw = W - P.l - P.r;
    const ih = H - P.t - P.b;
    const lo = min !== undefined ? min : 0;
    const hi = max !== undefined ? max : Math.max(...values) * 1.12;
    const range = hi - lo || 1;
    const x = (index: number) => P.l + (index / (values.length - 1)) * iw;
    const y = (value: number) => P.t + ih - ((value - lo) / range) * ih;
    const points = values.map((value, index): [number, number] => [x(index), y(value)]);
    const line = points
        .map((point, index) => `${index === 0 ? "M" : "L"}${point[0].toFixed(1)} ${point[1].toFixed(1)}`)
        .join(" ");
    const area =
        `M${x(0).toFixed(1)} ${(P.t + ih).toFixed(1)} ` +
        points.map((point) => `L${point[0].toFixed(1)} ${point[1].toFixed(1)}`).join(" ") +
        ` L${x(values.length - 1).toFixed(1)} ${(P.t + ih).toFixed(1)} Z`;
    const gridLines = [0, 1, 2, 3].map((step) => P.t + (ih / 3) * step);

    return (
        <svg
            viewBox={`0 0 ${W} ${H}`}
            width="100%"
            preserveAspectRatio="xMidYMid meet"
            style={{ display: "block", overflow: "visible" }}
        >
            <defs>
                <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0" stopColor={color} stopOpacity="0.30" />
                    <stop offset="1" stopColor={color} stopOpacity="0.02" />
                </linearGradient>
            </defs>
            {gridLines.map((gy) => (
                <line
                    key={gy}
                    x1={P.l}
                    y1={gy.toFixed(1)}
                    x2={W - P.r}
                    y2={gy.toFixed(1)}
                    stroke={CHART_COLORS.grid}
                    strokeWidth={1}
                />
            ))}
            <path d={area} fill={`url(#${gradientId})`} />
            <path
                d={line}
                fill="none"
                stroke={color}
                strokeWidth={2.5}
                strokeLinecap="round"
                strokeLinejoin="round"
            />
            {points.map((point, index) => {
                const tip =
                    tipFor !== undefined
                        ? tipFor(index)
                        : `<b>${labels[index]}</b><br>${formatValue(values[index])}`;
                return (
                    <g key={labels[index]}>
                        <circle
                            cx={point[0].toFixed(1)}
                            cy={point[1].toFixed(1)}
                            r={4}
                            fill={color}
                            stroke="#1A2128"
                            strokeWidth={2}
                        />
                        <rect
                            x={(point[0] - iw / values.length / 2).toFixed(1)}
                            y={P.t}
                            width={(iw / values.length).toFixed(1)}
                            height={ih}
                            fill="transparent"
                            data-tip={tip}
                            style={{ cursor: "pointer" }}
                        />
                    </g>
                );
            })}
            {labels.map((label, index) => (
                <text
                    key={label}
                    x={x(index).toFixed(1)}
                    y={H - 8}
                    fill={CHART_COLORS.axis}
                    fontSize={11}
                    fontWeight={500}
                    textAnchor="middle"
                >
                    {label}
                </text>
            ))}
        </svg>
    );
};
